
import { supabase } from '@/integrations/supabase/client';
import { CourseCompletionService } from './courseCompletionService';

interface CertificateStats {
  totalIssued: number;
  recentlyIssued: number;
  pendingGeneration: number;
  averageScore: number;
}

interface PendingCertificate {
  userId: string;
  userName: string;
  courseId: string;
  courseTitle: string;
  completionDate: string;
  quizAverage: number;
  canIssue: boolean;
  reason?: string;
}

interface CertificateMetadata {
  quiz_average?: number;
  [key: string]: any;
}

export class AdminCertificateService {
  // Get certificate statistics for admin dashboard
  static async getCertificateStats(): Promise<CertificateStats> {
    try {
      // Total issued certificates
      const { count: totalIssued } = await supabase
        .from('certificates')
        .select('*', { count: 'exact', head: true });

      // Recently issued (last 7 days)
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      
      const { count: recentlyIssued } = await supabase
        .from('certificates')
        .select('*', { count: 'exact', head: true })
        .gte('issue_date', sevenDaysAgo.toISOString());

      // Pending generation (completed courses without certificates)
      const { data: completedCourses } = await supabase
        .from('course_assignments')
        .select('user_id, course_id')
        .eq('status', 'completed')
        .gte('final_grade', 100);

      let pendingCount = 0;
      if (completedCourses) {
        for (const assignment of completedCourses) {
          const { data: existing } = await supabase
            .from('certificates')
            .select('id')
            .eq('user_id', assignment.user_id)
            .eq('course_id', assignment.course_id)
            .maybeSingle();
          
          if (!existing) {
            pendingCount++;
          }
        }
      }

      // Average score from metadata
      const { data: certificates } = await supabase
        .from('certificates')
        .select('metadata');

      const scores = certificates
        ?.map(cert => {
          const metadata = cert.metadata as CertificateMetadata;
          return metadata?.quiz_average;
        })
        .filter((score): score is number => typeof score === 'number') || [];
      
      const averageScore = scores.length > 0 
        ? scores.reduce((sum, score) => sum + score, 0) / scores.length 
        : 0;

      return {
        totalIssued: totalIssued || 0,
        recentlyIssued: recentlyIssued || 0,
        pendingGeneration: pendingCount,
        averageScore: Math.round(averageScore * 100) / 100
      };
    } catch (error) {
      console.error('Error getting certificate stats:', error);
      return {
        totalIssued: 0,
        recentlyIssued: 0,
        pendingGeneration: 0,
        averageScore: 0
      };
    }
  }

  // Get list of pending certificates that can be generated
  static async getPendingCertificates(): Promise<PendingCertificate[]> {
    try {
      const { data: completedAssignments } = await supabase
        .from('course_assignments')
        .select(`
          user_id,
          course_id,
          completion_date,
          final_grade
        `)
        .eq('status', 'completed')
        .gte('final_grade', 100);

      const pendingCertificates: PendingCertificate[] = [];

      if (completedAssignments) {
        for (const assignment of completedAssignments) {
          // Check if certificate already exists
          const { data: existing } = await supabase
            .from('certificates')
            .select('id')
            .eq('user_id', assignment.user_id)
            .eq('course_id', assignment.course_id)
            .maybeSingle();

          if (!existing) {
            // Get user profile
            const { data: profile } = await supabase
              .from('profiles')
              .select('name')
              .eq('id', assignment.user_id)
              .maybeSingle();

            // Validate if certificate can be issued - use static import to fix chunk splitting
            const { validateCourseCompletionForCertificate } = await import('@/hooks/auth/progressService');
            const validation = await validateCourseCompletionForCertificate(
              assignment.user_id,
              assignment.course_id
            );

            pendingCertificates.push({
              userId: assignment.user_id,
              userName: profile?.name || 'Unknown',
              courseId: assignment.course_id,
              courseTitle: assignment.course_id, // Should be enhanced with actual course titles
              completionDate: assignment.completion_date || new Date().toISOString(),
              quizAverage: validation.quizAverage || 0,
              canIssue: validation.canIssue,
              reason: validation.reason
            });
          }
        }
      }

      return pendingCertificates;
    } catch (error) {
      console.error('Error getting pending certificates:', error);
      return [];
    }
  }

  // Bulk generate certificates for eligible users
  static async bulkGenerateCertificates(pendingCertificates: PendingCertificate[]): Promise<{
    success: number;
    failed: number;
    errors: string[];
  }> {
    let success = 0;
    let failed = 0;
    const errors: string[] = [];

    for (const pending of pendingCertificates.filter(p => p.canIssue)) {
      try {
        const result = await CourseCompletionService.checkAndGenerateCertificate(
          pending.userId,
          pending.courseId,
          'unknown', // Should be enhanced with proper course type detection
          pending.courseTitle,
          pending.userName
        );

        if (result.success) {
          success++;
        } else {
          failed++;
          errors.push(`${pending.userName} (${pending.courseId}): ${result.message}`);
        }
      } catch (error) {
        failed++;
        errors.push(`${pending.userName} (${pending.courseId}): System error`);
      }
    }

    return { success, failed, errors };
  }

  // Set certificate pass threshold for a course (future enhancement)
  static async setCoursePassThreshold(courseId: string, threshold: number): Promise<boolean> {
    try {
      // This would require a course_settings table or similar
      // For now, we'll use a simple configuration approach
      console.log(`Setting pass threshold for ${courseId} to ${threshold}%`);
      return true;
    } catch (error) {
      console.error('Error setting course pass threshold:', error);
      return false;
    }
  }
}
